// Composable para lazy loading optimizado de componentes pesados
import { ref, defineAsyncComponent, computed, type Component } from 'vue'

// Interfaz para configuración de lazy loading
interface LazyLoadOptions {
  loadingComponent?: Component
  errorComponent?: Component
  delay?: number
  timeout?: number
  suspensible?: boolean
  retryDelay?: number
  maxRetries?: number
  onError?: (error: Error) => void
  onLoading?: () => void
  onLoaded?: () => void
}

// Configuración por defecto
const defaultOptions: LazyLoadOptions = {
  delay: 200,
  timeout: 30000,
  suspensible: false,
  retryDelay: 1000,
  maxRetries: 3
}

// Componente de loading por defecto
const DefaultLoadingComponent = {
  template: `
    <div class="lazy-loading-container">
      <v-progress-circular
        indeterminate
        color="primary"
        size="32"
      ></v-progress-circular>
      <p class="text-body-2 mt-2 text-grey">Cargando componente...</p>
    </div>
  `,
  style: `
    .lazy-loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      min-height: 200px;
    }
  `
}

// Componente de error por defecto
const DefaultErrorComponent = {
  props: ['error', 'retry'],
  template: `
    <div class="lazy-error-container">
      <v-alert
        type="error"
        variant="tonal"
        class="ma-4"
      >
        <template #title>
          Error al cargar componente
        </template>
        <template #text>
          {{ error?.message || 'Error desconocido al cargar el componente' }}
        </template>
        <template #append>
          <v-btn
            color="error"
            variant="outlined"
            size="small"
            @click="retry"
          >
            Reintentar
          </v-btn>
        </template>
      </v-alert>
    </div>
  `,
  style: `
    .lazy-error-container {
      min-height: 200px;
      display: flex;
      align-items: center;
    }
  `
}

/**
 * Composable para lazy loading de componentes con optimizaciones avanzadas
 */
export const useLazyComponent = (
  loader: () => Promise<any>,
  options: LazyLoadOptions = {}
) => {
  const config = { ...defaultOptions, ...options }
  const isLoading = ref(false)
  const hasError = ref(false)
  const error = ref<Error | null>(null)
  const retryCount = ref(0)

  // Función de retry
  const retry = () => {
    hasError.value = false
    error.value = null
    retryCount.value++
  }

  // Crear el componente lazy con configuración avanzada
  const lazyComponent = defineAsyncComponent({
    loader: async () => {
      isLoading.value = true
      hasError.value = false
      error.value = null
      
      config.onLoading?.()

      try {
        const component = await loader()
        config.onLoaded?.()
        return component
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error('Error de carga')
        error.value = errorObj
        hasError.value = true
        config.onError?.(errorObj)
        
        // Auto-retry logic
        if (retryCount.value < (config.maxRetries || 0)) {
          await new Promise(resolve => setTimeout(resolve, config.retryDelay))
          retryCount.value++
          return loader()
        }
        
        throw errorObj
      } finally {
        isLoading.value = false
      }
    },
    loadingComponent: config.loadingComponent || DefaultLoadingComponent,
    errorComponent: config.errorComponent || {
      ...DefaultErrorComponent,
      setup() {
        return { retry }
      }
    },
    delay: config.delay,
    timeout: config.timeout,
    suspensible: config.suspensible
  })

  return {
    component: lazyComponent,
    isLoading: computed(() => isLoading.value),
    hasError: computed(() => hasError.value),
    error: computed(() => error.value),
    retryCount: computed(() => retryCount.value),
    retry
  }
}

/**
 * Factory para crear lazy loaders específicos de formularios
 */
export const createFormLazyLoaders = () => {
  // Lazy loader para SmartIncidentForm
  const useSmartIncidentForm = (options?: LazyLoadOptions) => {
    return useLazyComponent(
      () => import('@/components/forms/SmartIncidentForm.vue'),
      {
        delay: 100, // Más rápido para componentes críticos
        timeout: 20000,
        ...options,
        onLoading: () => {
          console.log('🔄 Cargando SmartIncidentForm...')
          options?.onLoading?.()
        },
        onLoaded: () => {
          console.log('✅ SmartIncidentForm cargado exitosamente')
          options?.onLoaded?.()
        },
        onError: (error) => {
          console.error('❌ Error cargando SmartIncidentForm:', error)
          options?.onError?.(error)
        }
      }
    )
  }

  // Lazy loader para UserFormDialog
  const useUserFormDialog = (options?: LazyLoadOptions) => {
    return useLazyComponent(
      () => import('@/components/forms/UserFormDialog.vue'),
      {
        delay: 150,
        timeout: 25000,
        ...options,
        onLoading: () => {
          console.log('🔄 Cargando UserFormDialog...')
          options?.onLoading?.()
        },
        onLoaded: () => {
          console.log('✅ UserFormDialog cargado exitosamente')
          options?.onLoaded?.()
        }
      }
    )
  }

  // Lazy loader para InventoryRequestForm
  const useInventoryRequestForm = (options?: LazyLoadOptions) => {
    return useLazyComponent(
      () => import('@/components/forms/InventoryRequestForm.vue'),
      {
        delay: 200,
        timeout: 30000, // Más tiempo por ser un componente complejo
        ...options,
        onLoading: () => {
          console.log('🔄 Cargando InventoryRequestForm...')
          options?.onLoading?.()
        },
        onLoaded: () => {
          console.log('✅ InventoryRequestForm cargado exitosamente')
          options?.onLoaded?.()
        }
      }
    )
  }

  return {
    useSmartIncidentForm,
    useUserFormDialog,
    useInventoryRequestForm
  }
}

/**
 * Preloader para precarga de componentes críticos
 */
export const useComponentPreloader = () => {
  const preloadedComponents = ref<Set<string>>(new Set())
  const preloading = ref<Set<string>>(new Set())

  const preloadComponent = async (
    name: string,
    loader: () => Promise<any>
  ): Promise<boolean> => {
    if (preloadedComponents.value.has(name)) {
      return true
    }

    if (preloading.value.has(name)) {
      return false
    }

    preloading.value.add(name)

    try {
      await loader()
      preloadedComponents.value.add(name)
      console.log(`✅ Componente ${name} precargado exitosamente`)
      return true
    } catch (error) {
      console.error(`❌ Error precargando componente ${name}:`, error)
      return false
    } finally {
      preloading.value.delete(name)
    }
  }

  const preloadFormComponents = async () => {
    const loaders = [
      { 
        name: 'SmartIncidentForm', 
        loader: () => import('@/components/forms/SmartIncidentForm.vue') 
      },
      { 
        name: 'UserFormDialog', 
        loader: () => import('@/components/forms/UserFormDialog.vue') 
      },
      { 
        name: 'InventoryRequestForm', 
        loader: () => import('@/components/forms/InventoryRequestForm.vue') 
      }
    ]

    const preloadPromises = loaders.map(({ name, loader }) => 
      preloadComponent(name, loader)
    )

    const results = await Promise.allSettled(preloadPromises)
    
    const successful = results.filter(result => 
      result.status === 'fulfilled' && result.value === true
    ).length

    console.log(`📦 Precarga completada: ${successful}/${loaders.length} componentes`)
    
    return successful === loaders.length
  }

  const isPreloaded = (name: string) => preloadedComponents.value.has(name)
  const isPreloading = (name: string) => preloading.value.has(name)

  return {
    preloadComponent,
    preloadFormComponents,
    isPreloaded,
    isPreloading,
    preloadedComponents: computed(() => Array.from(preloadedComponents.value)),
    preloadingComponents: computed(() => Array.from(preloading.value))
  }
}

/**
 * Hook para detección de características del dispositivo para optimización
 */
export const useDeviceOptimization = () => {
  const isLowEnd = ref(false)
  const connection = ref<any>(null)
  const shouldPreload = ref(true)

  // Detectar dispositivos de gama baja
  const detectLowEndDevice = () => {
    // Navigator.hardwareConcurrency (número de cores)
    const cores = navigator.hardwareConcurrency || 1
    
    // Navigator.deviceMemory (GB de RAM)
    const memory = (navigator as any).deviceMemory || 1
    
    // Detectar dispositivos con recursos limitados
    isLowEnd.value = cores <= 2 || memory <= 2
    
    // En dispositivos de gama baja, desactivar precarga
    if (isLowEnd.value) {
      shouldPreload.value = false
    }
  }

  // Detectar tipo de conexión
  const detectConnection = () => {
    const conn = (navigator as any).connection || 
                  (navigator as any).mozConnection || 
                  (navigator as any).webkitConnection

    if (conn) {
      connection.value = {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
        saveData: conn.saveData
      }

      // En conexiones lentas o con ahorro de datos, limitar precarga
      if (conn.effectiveType === 'slow-2g' || 
          conn.effectiveType === '2g' || 
          conn.saveData) {
        shouldPreload.value = false
      }
    }
  }

  // Inicializar detección
  detectLowEndDevice()
  detectConnection()

  const getOptimizationConfig = (): LazyLoadOptions => {
    const baseConfig: LazyLoadOptions = {
      delay: 200,
      timeout: 30000
    }

    if (isLowEnd.value) {
      return {
        ...baseConfig,
        delay: 500, // Mayor delay en dispositivos lentos
        timeout: 60000, // Más tiempo de timeout
        maxRetries: 1 // Menos reintentos
      }
    }

    if (connection.value?.effectiveType === '2g' || connection.value?.saveData) {
      return {
        ...baseConfig,
        delay: 300,
        timeout: 45000,
        maxRetries: 2
      }
    }

    return baseConfig
  }

  return {
    isLowEnd: computed(() => isLowEnd.value),
    connection: computed(() => connection.value),
    shouldPreload: computed(() => shouldPreload.value),
    getOptimizationConfig
  }
}

export default {
  useLazyComponent,
  createFormLazyLoaders,
  useComponentPreloader,
  useDeviceOptimization
}
